import getSupplierProductCategoriesApi from '@/api/get/company/sales-flow/get-supplier-product-categories';
import updateCompanyProductCategoryOrderApi from '@/api/patch/company/company-product-category/update-company-product-category-order';
import Alert from '@/components/alert/alert-component';
import SubmitCard from '@/components/card/submit-card-component';
import { Row } from '@/components/grid/grid-component';
import { Col } from '@/components/table/components/grid/grid-component';
import { useMutation, useQuery } from '@/hooks/use-api';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdOutlineDragIndicator } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import Styled from './reorder-style';

/**
 * Reorder product portal component.
 * @param {object} props The component props.
 * @param {object} props.children The children to render.
 * @returns {null|JSX.Element} The rendered ReorderProductPortal component.
 */
const ReorderProductPortal = ({ children }) => {
  const reorderProductPortal = document.getElementById('reorderProductPortal');
  if (reorderProductPortal) {
    return createPortal(children, reorderProductPortal);
  }
  return null;
};

ReorderProductPortal.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Reorder products component.
 * @param {object} props The component props.
 * @param {Function} props.onClose The function to close the modal.
 * @returns {JSX.Element} The rendered Reorder Products page component.
 */
const ReorderProducts = ({ onClose }) => {
  const { id } = useParams();
  const [productCategories, setProductCategories] = useState([]);
  const containerRef = useRef(null);
  const { data: productCategoriesData, loading: productCategoriesLoading } =
    useQuery(getSupplierProductCategoriesApi, id);

  const [
    updateCompanyProductCategoryOrder,
    { loading: updateCompanyProductCategoryOrderLoading },
  ] = useMutation(updateCompanyProductCategoryOrderApi);

  useEffect(() => {
    if (productCategoriesData) {
      setProductCategories(productCategoriesData);
    }
  }, [productCategoriesData]);

  // Set up drag and drop for individual items
  const setupDraggable = (element, index) => {
    if (!element) return;

    const cleanup = draggable({
      element,
      getInitialData: () => ({ index: index.toString() }),
    });

    return cleanup;
  };

  // Set up drop target for individual items
  const setupDropTarget = (element, index) => {
    if (!element) return;

    const cleanup = dropTargetForElements({
      element,
      getData: () => ({ index: index.toString() }),
      onDrop: ({ source }) => {
        const sourceIndex = parseInt(source.data.index, 10);
        const targetIndex = index;

        if (sourceIndex !== targetIndex) {
          const newProductCategories = [...productCategories];
          const [movedItem] = newProductCategories.splice(sourceIndex, 1);
          newProductCategories.splice(targetIndex, 0, movedItem);
          setProductCategories(newProductCategories);
        }
      },
    });

    return cleanup;
  };

  return (
    <Styled.ReorderProductsContainer>
      <Row ref={containerRef} className="draggable-container">
        {!productCategoriesLoading &&
          productCategories?.map((product, idx) => (
            <Col
              className="draggable-item"
              key={`${product.name}_${idx + 1}`}
              sm={6}
              md={4}
              lg={4}
              xl={3}
              xxl={3}
              $marginBottom="30px"
            >
              <Styled.ProductFilterGroupItem
                ref={(element) => {
                  if (element) {
                    const draggableCleanup = setupDraggable(element, idx);
                    const dropTargetCleanup = setupDropTarget(element, idx);

                    // Return a cleanup function that cleans up both
                    return () => {
                      draggableCleanup?.();
                      dropTargetCleanup?.();
                    };
                  }
                }}
              >
                <Styled.DragIndicator>
                  <MdOutlineDragIndicator />
                </Styled.DragIndicator>
                <Styled.ImageHolder>
                  {product.thumbnail ? (
                    <Styled.ProductFilterGroupItemImage
                      src={product.thumbnail}
                      alt={product.name}
                    />
                  ) : (
                    <Styled.NoThumbnail />
                  )}
                </Styled.ImageHolder>
                <Styled.ProductFilterGroupItemDescription>
                  {product.main_category}
                </Styled.ProductFilterGroupItemDescription>
                <Styled.ProductFilterGroupItemTitle>
                  <Styled.TitleItems>{product.name}</Styled.TitleItems>
                </Styled.ProductFilterGroupItemTitle>
              </Styled.ProductFilterGroupItem>
            </Col>
          ))}
      </Row>

      <SubmitCard
        padding="0"
        primaryButtonTitle="Update Order"
        primaryButtonType="submit"
        primaryButtonDisabled={false}
        primaryButtonWidth="152px"
        submitClick={() => {
          const newOrder = productCategories.map((product, index) => ({
            id: product.id,
            order: index,
          }));
          updateCompanyProductCategoryOrder(id, {
            productCategories: newOrder,
          })
            .then(() => {
              onClose();
              Alert({
                icon: 'success',
                text: 'Product order updated successfully',
              });
            })
            .catch(() => {
              Alert({
                icon: 'error',
                text: 'Product order failed to update',
              });
            });
        }}
        fixToBottom
        submitClickSecondary={() => {
          onClose();
        }}
        isLoading={updateCompanyProductCategoryOrderLoading}
        secondaryButtonType="button"
        secondaryButtonDisabled={false}
        secondaryButtonWidth="112px"
        disabled={false}
        secondaryButtonTitle="Cancel"
      />
    </Styled.ReorderProductsContainer>
  );
};

ReorderProducts.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default ReorderProducts;
